import { Email } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

export type UpgradeModalType = 'wallet' | 'fund' | 'follow' | 'approval';

export type ParsedMonitorLimitError = {
  resource: 'carteiras' | 'fundos' | 'follow';
  current: number;
  limit: number;
  adding?: number;
};

type UpgradeModalProps = {
  open: boolean;
  onClose: () => void;
  type: UpgradeModalType;
  maxLimit: number | null;
  currentCount: number;
  attemptedCount?: number;
  /** When present, usage/limit are taken from the API error (authoritative). */
  apiMessage?: string | null;
  userName?: string;
  userId?: number;
};

const LIMIT_UNIT: Record<
  UpgradeModalType,
  { plural: string; usageLabel: string }
> = {
  wallet: {
    plural: 'carteira(s)',
    usageLabel: 'Carteiras em uso na assinatura',
  },
  fund: {
    plural: 'fundo(s)',
    usageLabel: 'Fundos em uso nesta carteira',
  },
  follow: {
    plural: 'carteira(s)/FIC(s) seguido(s)',
    usageLabel: 'Uso de carteiras/FICs na assinatura',
  },
  approval: {
    plural: 'carteira(s)',
    usageLabel: 'Carteiras em uso na assinatura',
  },
};

export function parseMonitorLimitError(
  message: string | null | undefined,
): ParsedMonitorLimitError | null {
  if (!message) return null;

  if (/não permite seguir fundos FIC/i.test(message)) {
    return { resource: 'follow', current: 0, limit: 0 };
  }

  const usageMatch = message.match(
    /Limite de (carteiras|fundos) excedido\.\s*Uso atual:\s*(\d+)\s*de\s*(\d+)\.(?:\s*Tentativa de adicionar:\s*(\d+)\.)?/i,
  );
  if (usageMatch) {
    return {
      resource: usageMatch[1].toLowerCase() as 'carteiras' | 'fundos',
      current: Number(usageMatch[2]),
      limit: Number(usageMatch[3]),
      adding: usageMatch[4] !== undefined ? Number(usageMatch[4]) : undefined,
    };
  }

  const legacyMatch = message.match(
    /Limite de (carteiras|fundos) excedido\.\s*Permitido:\s*(\d+),\s*Tentando criar:\s*(\d+)/i,
  );
  if (legacyMatch) {
    const limit = Number(legacyMatch[2]);
    const adding = Number(legacyMatch[3]);
    return {
      resource: legacyMatch[1].toLowerCase() as 'carteiras' | 'fundos',
      current: Math.max(0, limit),
      limit,
      adding,
    };
  }

  return null;
}

export function isMonitorPlanLimitError(message: string | undefined | null) {
  if (!message) return false;
  return (
    /Limite de (carteiras|fundos) excedido/i.test(message) ||
    /não permite seguir fundos FIC/i.test(message)
  );
}

export function resolveUpgradeModalTypeFromMessage(
  message: string,
): UpgradeModalType {
  const parsed = parseMonitorLimitError(message);
  if (
    parsed?.resource === 'follow' ||
    /não permite seguir fundos FIC/i.test(message)
  ) {
    return 'follow';
  }
  if (
    parsed?.resource === 'fundos' ||
    /Limite de fundos excedido/i.test(message)
  ) {
    return 'fund';
  }
  return 'wallet';
}

function getCopy({
  type,
  maxLimit,
  currentCount,
  attemptedCount,
  followDisabled,
}: {
  type: UpgradeModalType;
  maxLimit: number | null;
  currentCount: number;
  attemptedCount?: number;
  followDisabled: boolean;
}) {
  const hasNumericLimit = typeof maxLimit === 'number';

  switch (type) {
    case 'wallet':
      return {
        title: 'Limite de carteiras atingido',
        description: hasNumericLimit
          ? `Você atingiu o limite de ${maxLimit} carteira(s) da assinatura (uso atual: ${currentCount} de ${maxLimit}). Para criar outra carteira, é necessário fazer upgrade do plano.`
          : 'Você atingiu o limite de carteiras da assinatura. Para criar outra carteira, é necessário fazer upgrade do plano.',
        mailFocus: 'carteiras do Monitor',
      };
    case 'fund':
      return {
        title: 'Limite de fundos atingido',
        description: hasNumericLimit
          ? attemptedCount
            ? `Você está tentando adicionar ${attemptedCount} fundo(s), mas atingiu o limite de ${maxLimit} fundo(s) nesta carteira (uso atual: ${currentCount} de ${maxLimit}). Para continuar, é necessário fazer upgrade do plano.`
            : `Você atingiu o limite de ${maxLimit} fundo(s) nesta carteira (uso atual: ${currentCount} de ${maxLimit}). Para adicionar mais fundos, é necessário fazer upgrade do plano.`
          : 'Você atingiu o limite de fundos nesta carteira. Para adicionar mais fundos, é necessário fazer upgrade do plano.',
        mailFocus: 'fundos por carteira do Monitor',
      };
    case 'follow':
      return {
        title: followDisabled
          ? 'Plano sem permissão para seguir FICs'
          : 'Limite para seguir FICs atingido',
        description: followDisabled
          ? 'Seu plano não permite seguir fundos FIC. Entre em contato com o comercial para conhecer planos com esse recurso.'
          : hasNumericLimit
            ? `Você atingiu o limite de ${maxLimit} carteira(s)/FIC(s) seguidos da assinatura (uso atual: ${currentCount} de ${maxLimit}). Para seguir outro fundo, é necessário fazer upgrade do plano.`
            : 'Você atingiu o limite de carteiras/FICs seguidos da assinatura. Para seguir outro fundo, é necessário fazer upgrade do plano.',
        mailFocus: 'acompanhamento de FICs no Monitor',
      };
    case 'approval':
      return {
        title: 'Não foi possível aprovar a carteira',
        description: hasNumericLimit
          ? `A aprovação não foi concluída porque a assinatura atingiu o limite de ${maxLimit} carteira(s) (uso atual: ${currentCount} de ${maxLimit}). Ajuste o uso ou faça upgrade do plano para aprovar.`
          : 'A aprovação não foi concluída porque a assinatura atingiu um limite do plano (carteiras ou fundos na carteira). Ajuste o uso ou faça upgrade do plano para aprovar.',
        mailFocus: 'limites do Monitor para aprovar carteiras de convidados',
      };
    default:
      return {
        title: 'Limite do plano atingido',
        description:
          'Você atingiu um limite do seu plano no Monitor. Entre em contato com o comercial para aumentar a capacidade.',
        mailFocus: 'limites do Monitor',
      };
  }
}

export function UpgradeModal({
  open,
  onClose,
  type,
  maxLimit,
  currentCount,
  attemptedCount,
  apiMessage,
  userName = '',
  userId,
}: UpgradeModalProps) {
  const parsed = parseMonitorLimitError(apiMessage);
  const followDisabled = parsed?.resource === 'follow' && parsed.limit === 0;

  // Prefer numbers from the API error — context may be stale/unloaded (null/0).
  const resolvedMaxLimit =
    parsed && parsed.resource !== 'follow'
      ? parsed.limit
      : followDisabled
        ? 0
        : maxLimit;
  const resolvedCurrentCount =
    parsed && parsed.resource !== 'follow'
      ? parsed.current
      : followDisabled
        ? 0
        : currentCount;
  const resolvedAttempted = attemptedCount ?? parsed?.adding ?? undefined;

  const displayType: UpgradeModalType =
    type === 'approval'
      ? 'approval'
      : parsed?.resource === 'fundos'
        ? 'fund'
        : parsed?.resource === 'follow'
          ? 'follow'
          : type === 'follow'
            ? 'follow'
            : type === 'fund'
              ? 'fund'
              : 'wallet';

  // Approval blocked by funds: keep type approval but show fund units in the box.
  const unitType: UpgradeModalType =
    type === 'approval' && parsed?.resource === 'fundos' ? 'fund' : displayType;

  const unit = LIMIT_UNIT[unitType];
  // Keep the entry-point type for title/copy (follow/approval), even if the
  // API message mentions fundos vs carteiras — the usage box uses unitType.
  const copyType: UpgradeModalType =
    type === 'approval' || type === 'follow' ? type : displayType;
  const { title, description, mailFocus } = getCopy({
    type: copyType,
    maxLimit: resolvedMaxLimit,
    currentCount: resolvedCurrentCount,
    attemptedCount: resolvedAttempted,
    followDisabled,
  });

  const approvalFundDescription =
    type === 'approval' &&
    parsed?.resource === 'fundos' &&
    typeof resolvedMaxLimit === 'number'
      ? `A aprovação não foi concluída porque a carteira excede o limite de ${resolvedMaxLimit} fundo(s) (uso atual: ${resolvedCurrentCount} de ${resolvedMaxLimit}). Ajuste os fundos ou faça upgrade do plano para aprovar.`
      : null;

  const handleContactSales = () => {
    const subject = 'Solicitação de Upgrade - Limites do Monitor';
    const limitLabel =
      resolvedMaxLimit === null
        ? 'não informado'
        : resolvedMaxLimit === 0 && followDisabled
          ? 'recurso desabilitado'
          : String(resolvedMaxLimit);

    const bodyLines = [
      'Olá, equipe comercial!',
      '',
      `Gostaria de solicitar um upgrade do plano para aumentar os limites de ${mailFocus}.`,
      '',
      `Tipo de limite: ${type}`,
      `Limite atual: ${limitLabel}`,
      `${unit.usageLabel}: ${resolvedCurrentCount}`,
    ];

    if (resolvedAttempted != null) {
      bodyLines.push(`Tentativa: ${resolvedAttempted}`);
    }

    if (apiMessage) {
      bodyLines.push('', `Mensagem do sistema: ${apiMessage}`);
    }

    if (userName && userId) {
      bodyLines.push(
        '',
        'Dados do usuário:',
        `Nome: ${userName}`,
        `ID: ${userId}`,
      );
    }

    bodyLines.push(
      '',
      'Por favor, entrem em contato para discutir as opções disponíveis.',
      '',
      'Obrigado!',
    );

    const body = bodyLines.join('%0D%0A');
    const mailtoLink = `mailto:comercial@uqbar.com.br?subject=${encodeURIComponent(subject)}&body=${body}`;

    window.location.href = mailtoLink;
    onClose();
  };

  const showUsageBox = !followDisabled;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="monitor-upgrade-modal-title">
      <DialogTitle id="monitor-upgrade-modal-title">{title}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="body1">
            {approvalFundDescription || description}
          </Typography>

          {showUsageBox && (
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                mt: 1,
              }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Limite atual:</strong>{' '}
                {typeof resolvedMaxLimit === 'number'
                  ? `${resolvedMaxLimit} ${unit.plural}`
                  : 'Não informado'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Em uso:</strong> {resolvedCurrentCount} {unit.plural}
              </Typography>
              {resolvedAttempted != null && unitType === 'fund' && (
                <Typography variant="body2" color="textSecondary">
                  <strong>Tentando adicionar:</strong> {resolvedAttempted}{' '}
                  fundo(s)
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="body2" color="textSecondary" mt={1}>
            Entre em contato com nosso time comercial para conhecer os planos
            disponíveis e aumentar seus limites.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Fechar
        </Button>
        <Button
          onClick={handleContactSales}
          color="primary"
          variant="contained"
          startIcon={<Email />}>
          Falar com Time Comercial
        </Button>
      </DialogActions>
    </Dialog>
  );
}
